import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import useDeletePropertyOwnershipMutation from 'components/domain/owner/useDeletePropertyOwnershipMutation';
import { modalIds } from 'components/modal/Modal.constants';
import { TextHighlightGreen } from 'components/text/TextHighlightGreen';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { PropertyOwner } from 'models/Owner';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { PROPERTY_OWNERS_BASE_QUERY_KEY } from '../../usePropertyOwners';

const useRemovePropertyAccessModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { property } = usePropertySettingsInitData();

  const { notifySuccess } = useNotify();
  const { openConfirmModal, closeModal, setErrorConfirmModal } = useAppModal();
  const { mutateAsync: deletePropertyOwnership } =
    useDeletePropertyOwnershipMutation({
      onSuccess: () => {
        notifySuccess(
          t('pageProperty.owners.removePropertyAccessModal.success'),
        );
        closeModal(modalIds.confirm);
        queryClient.invalidateQueries({
          queryKey: [PROPERTY_OWNERS_BASE_QUERY_KEY, 'detail', property.uid],
        });
      },
      onError: (error) => {
        setErrorConfirmModal(
          getErrorMessage({
            apiErrorMessage: error.response?.data.apiErrorMessage ?? '',
            apiErrorCode: error.response?.data.apiErrorCode,
            baseKey: 'pageProperty.apiErrorCodes.',
            t,
          }),
        );
      },
    });

  const handleRemovePropertyAccess = async () => {
    await deletePropertyOwnership({ propertyUid: property.uid! });
  };

  const openRemovePropertyAccessModal = (owner: PropertyOwner) => {
    const ownerName = [owner.firstName, owner.lastName]
      .filter(Boolean)
      .join(' ');

    openConfirmModal({
      body: (
        <>
          <Trans
            i18nKey="pageProperty.owners.removePropertyAccessModal.description"
            values={{ property: property.name, owner: ownerName }}
            components={{ highlight: <TextHighlightGreen /> }}
          />
          <br />
          {t(
            'pageProperty.owners.removePropertyAccessModal.ownerWillNotLongerHaveAccess',
          )}
        </>
      ),
      onConfirmAsync: handleRemovePropertyAccess,
      title: t('pageProperty.owners.removeOwner'),
      confirmLabel: t('pageProperty.owners.removePropertyAccessModal.confirm'),
      confirmColor: 'danger',
    });
  };

  return {
    openRemovePropertyAccessModal,
  };
};

export default useRemovePropertyAccessModal;
